import { EditorModes } from '../../../code/interfaces/editor';
import { DESTINATION_TYPES } from '../../../containers/destination/constants/destinationTypes';
import { matchPattern } from '../../../core/utils/string';
import { SchemaExplorerFoldersFactory } from '../factories/schemaFactory';
import { SchemaExplorerFolder } from '../interfaces/schemaExplorerFolder';

/*
 * Filters the folders and files based on the search text
 * and sorts them, priority being the collapsed ones
 *
 * It firsts scans the folders and then the not matching folders,
 * scanning the files to highlight the matched files
 * */
export function searchSchema(folders: SchemaExplorerFolder[], searchText: string): any[] {
  return folders
    .map(folder => ({ ...folder }))
    .filter(folder => {
      let matchFound = false;

      folder.collapsed = true;

      if (matchPattern(folder.name, searchText)) {
        matchFound = true;
      }

      folder.files = folder.files.map(file => ({ ...file, searchHighlight: false }));

      if (matchFound) {
        return matchFound;
      }

      const matchingFiles = folder.files.filter((field: any) => {
        const hasMatch = matchPattern(field.name, searchText);
        if (hasMatch) {
          field.searchHighlight = true;
        }

        return hasMatch;
      });

      if (matchingFiles.length) {
        folder.collapsed = false;
        matchFound = true;
      }

      return matchFound;
    })
    .sort((a, b) => (a.collapsed ? (b.collapsed ? 0 : -1) : 1));
}

/*
 * Creates a list of Schema Folders retaining the collapsed state from old list
 * */
export function getFolders(currentList: SchemaExplorerFolder[], rawSchemaData: any[]) {
  const newList = SchemaExplorerFoldersFactory(rawSchemaData);
  return retainCollapsedState(currentList, newList);
}

/*
 * Maintain the collapsed state of new list with the help
 * of old one when not appending the lists
 * */
function retainCollapsedState(
  currentList: SchemaExplorerFolder[],
  newList: SchemaExplorerFolder[]
) {
  return newList.map(folder => {
    const existingRef = currentList.find(existingFolder => folder.id === existingFolder.id);

    if (existingRef) {
      folder.collapsed = existingRef.collapsed;
    }

    return folder;
  });
}

/*
 * Fetch the next set of folders and files to render
 * based on the fact if one of the folder is collapsed or not
 * */
export function getNextRenderFoldersCount(folders, pageSize, startIndex) {
  let nextFoldersCount = 0;
  let nextRowsCount = 0;

  folders.slice(startIndex, folders.length).some(folder => {
    nextFoldersCount++;
    nextRowsCount++;

    if (!folder.collapsed) {
      nextRowsCount += folder.files.length;
    }

    return nextRowsCount >= pageSize;
  });

  return nextFoldersCount;
}

export function wrapInQuotes(value: string, quoteType: string) {
  return `${quoteType}${value}${quoteType}`;
}

export function getFullTableName(
  tableName: string,
  databaseName: string,
  schemaName: string,
  quoteType: string
) {
  if (schemaName && databaseName) {
    return `${wrapInQuotes(databaseName, quoteType)}.${wrapInQuotes(
      schemaName,
      quoteType
    )}.${wrapInQuotes(tableName, quoteType)}`;
  }

  if (databaseName) {
    return `${wrapInQuotes(databaseName, quoteType)}.${wrapInQuotes(tableName, quoteType)}`;
  }

  return tableName;
}

export function getQuoteType(destType: string, datasetName: string) {
  return DESTINATION_TYPES[destType].editorMode === EditorModes.MY_SQL || datasetName ? '`' : '"';
}
